import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'macd',
  name: 'MACD',
  shortName: 'MACD',
  category: 'momentum',
  overlay: false,
  params: [
    { key: 'fastPeriod', label: 'Fast', type: 'number', default: 12, min: 1, max: 200 },
    { key: 'slowPeriod', label: 'Slow', type: 'number', default: 26, min: 1, max: 200 },
    { key: 'signalPeriod', label: 'Signal', type: 'number', default: 9, min: 1, max: 200 },
  ],
  lines: [
    { key: 'macd', label: 'MACD', color: '#2196f3', width: 1 },
    { key: 'signal', label: 'Signal', color: '#ff5722', width: 1 },
  ],
  histogramDefs: [
    { key: 'hist', label: 'Histogram', colorPositive: '#26a69a', colorNegative: '#ef5350' },
  ],
}

/** EMA on a raw number array, returns aligned (number | null)[]. */
function calcEma(prices: number[], period: number): (number | null)[] {
  const k = 2 / (period + 1)
  const out: (number | null)[] = new Array<number | null>(prices.length).fill(null)
  let ema: number | null = null
  let seedSum = 0

  for (let i = 0; i < prices.length; i++) {
    if (ema === null) {
      seedSum += prices[i]
      if (i === period - 1) {
        ema = seedSum / period
        out[i] = ema
      }
    } else {
      ema = prices[i] * k + ema * (1 - k)
      out[i] = ema
    }
  }
  return out
}

class MACD extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const fastPeriod = Math.max(1, Number(params['fastPeriod']) || 12)
    const slowPeriod = Math.max(fastPeriod + 1, Number(params['slowPeriod']) || 26)
    const signalPeriod = Math.max(1, Number(params['signalPeriod']) || 9)

    const closes = candles.map((c) => c.c)
    const fastEma = calcEma(closes, fastPeriod)
    const slowEma = calcEma(closes, slowPeriod)

    // MACD line = fast EMA − slow EMA
    const macdLine: (number | null)[] = fastEma.map((f, i) => {
      const s = slowEma[i]
      return f !== null && s !== null ? f - s : null
    })

    // Signal line = EMA(macdLine, signalPeriod), calculated on the non-null values in order
    const signalLine: (number | null)[] = new Array<number | null>(candles.length).fill(null)
    const kSignal = 2 / (signalPeriod + 1)
    let sigEma: number | null = null
    let seedCount = 0
    let seedSum = 0

    for (let i = 0; i < macdLine.length; i++) {
      const m = macdLine[i]
      if (m === null) continue

      if (sigEma === null) {
        seedSum += m
        seedCount++
        if (seedCount === signalPeriod) {
          sigEma = seedSum / signalPeriod
          signalLine[i] = sigEma
        }
      } else {
        sigEma = m * kSignal + sigEma * (1 - kSignal)
        signalLine[i] = sigEma
      }
    }

    // Histogram = MACD − Signal
    const histValues: (number | null)[] = macdLine.map((m, i) => {
      const s = signalLine[i]
      return m !== null && s !== null ? m - s : null
    })
    const histColors: string[] = histValues.map((v) =>
      v === null || v >= 0 ? '#26a69a' : '#ef5350'
    )

    return {
      lines: [
        { key: 'macd', values: macdLine },
        { key: 'signal', values: signalLine },
      ],
      histograms: [{ values: histValues, colors: histColors }],
    }
  }
}

registerIndicator(MACD, CONFIG)
